import { Signal } from "@engine";
import type { LevelStreamer } from "../level/LevelStreamer";
import type { Player } from "../player/Player";
import type { WorldRotator } from "../world/WorldRotator";
import type { HUD } from "../ui/HUD";
import { panelIndexOf } from "../level/SegmentFactory";
import { shapeOf } from "../config/shapes";
import type { RotationVerdict } from "../audio/BeatTimingService";

const COMBO_PER_TIER = 3;
const MAX_MULTIPLIER = 8;
const PERFECT_BONUS = 50;
const GOOD_BONUS = 20;
const FIRE_STREAK = 3;

// Combo + multiplier + score. Listens to:
//   - Player.onWallContact: marks "grazed" if the contacted wall is NOT
//     the panel currently expected to be the floor (i.e., the player
//     touched a side wall, not just landed on the new floor).
//   - LevelStreamer.onSegmentCleared: resolves the combo for the segment
//     that just exited the play area.
//   - Player.onDied: resets the combo.
//
// External callers (e.g., PickupManager) can use addBonus() to award
// flat score without touching the combo. The onComboChanged signal is
// for systems that want to react to combo tier (the MusicDirector adds
// melodic layers as the player builds heat).
export class ScoreSystem {
  readonly onComboChanged = new Signal<number>();
  // Emitted when a perfect-rotation streak reaches FIRE_STREAK so
  // higher-level systems (audio, FX) can react. Payload = current
  // streak length so chain-of-N celebrations can ramp.
  readonly onPerfectStreak = new Signal<number>();
  // Emitted on every individual perfect rotation so StatsService can
  // bump perfectsTotal/perfectsThisRun without us needing to dig into
  // the streak signal (which only fires every FIRE_STREAK hits).
  readonly onPerfectRotation = new Signal<void>();

  score = 0;
  combo = 0;
  multiplier = 1;
  perfectStreak = 0;

  // External multiplier (Frenzy mode applies 2x here). Defaults to 1
  // and is set every frame by FrenzyMode.scoreMul().
  private extraScoreMul = 1;
  // Long-lived "reward" multiplier composed by PickupManager from the
  // current difficulty profile + any active double-score window. Set
  // ONCE at startGame for difficulty and re-set whenever the double
  // pickup window starts/ends. Applied to ALL score gains (segment,
  // pickup, perfect/good bonuses) so a Hard run with an active double
  // really does pay 1.25 * 2 = 2.5x baseline.
  private rewardMul = 1;

  private grazedThisSegment = false;
  private streamer: LevelStreamer;
  private player: Player;
  private rotator: WorldRotator;
  private hud: HUD;

  constructor(
    streamer: LevelStreamer,
    player: Player,
    rotator: WorldRotator,
    hud: HUD
  ) {
    this.streamer = streamer;
    this.player = player;
    this.rotator = rotator;
    this.hud = hud;

    this.player.onWallContact.on((wall) => {
      if (this.isGraze(wall)) this.grazedThisSegment = true;
    });

    this.streamer.onSegmentCleared.on((seg) => {
      if (this.grazedThisSegment) {
        this.combo = 0;
        this.multiplier = 1;
        this.hud.spawnPopup("GRAZE", "warm");
        this.onComboChanged.emit(this.combo);
      } else {
        this.combo += 1;
        this.multiplier = Math.min(
          MAX_MULTIPLIER,
          1 + Math.floor(this.combo / COMBO_PER_TIER)
        );
        const gain = Math.round(
          10 * seg.speed * this.multiplier * this.extraScoreMul * this.rewardMul
        );
        this.score += gain;
        this.hud.spawnPopup(
          this.multiplier > 1
            ? `+${gain}  x${this.multiplier}`
            : `+${gain}`,
          "accent"
        );
        this.onComboChanged.emit(this.combo);
      }
      this.grazedThisSegment = false;
      this.hud.setCombo(this.combo, this.multiplier);
    });

    this.player.onDied.on(() => {
      this.combo = 0;
      this.multiplier = 1;
      this.perfectStreak = 0;
      this.hud.setCombo(0, 1);
      this.onComboChanged.emit(0);
    });
  }

  // Flat score bonus that bypasses combo accounting. Used by pickups
  // (score orbs already reward via the combo mechanic indirectly --
  // staying alive longer = bigger combo = bigger orb bonus too).
  // Applies the long-lived reward multiplier (difficulty + double-pickup
  // window) so callers don't have to thread the multiplier through.
  addBonus(n: number): void {
    if (n <= 0) return;
    this.score += Math.round(n * this.rewardMul);
  }

  // Called by MorphHexGame for every rotate input with the verdict
  // returned by BeatTimingService.judge. "perfect" pays a hot bonus
  // and grows the perfect streak; "good" pays a softer bonus; "miss"
  // resets the streak silently (no popup -- the rotation itself is
  // its own feedback).
  registerRotationTiming(verdict: RotationVerdict): void {
    if (verdict === "perfect") {
      const bonus = Math.round(PERFECT_BONUS * this.multiplier);
      this.addBonus(bonus);
      this.hud.spawnPopup(`PERFECT +${bonus}`, "hot");
      this.perfectStreak += 1;
      // Per-rotation signal so StatsService bumps once per perfect.
      // The streak signal fires only every FIRE_STREAK hits, which
      // would under-count the perfectsTotal lifetime stat by 3x.
      this.onPerfectRotation.emit();
      if (this.perfectStreak === FIRE_STREAK) {
        this.hud.spawnPopup("FIRE!", "hot");
        this.onPerfectStreak.emit(this.perfectStreak);
      } else if (
        this.perfectStreak > FIRE_STREAK &&
        this.perfectStreak % FIRE_STREAK === 0
      ) {
        // Every additional 3-perfect milestone re-fires the streak
        // signal so listeners can keep ramping (more layers, hotter
        // sky pulse, etc.).
        this.onPerfectStreak.emit(this.perfectStreak);
      }
    } else if (verdict === "good") {
      const bonus = Math.round(GOOD_BONUS * this.multiplier);
      this.addBonus(bonus);
      this.hud.spawnPopup(`GOOD +${bonus}`, "accent");
      // "Good" doesn't break the streak (it's still on-beat), but
      // also doesn't grow it -- only true perfects do.
    } else {
      this.perfectStreak = 0;
    }
  }

  // Frenzy / boss systems write here each frame to scale segment
  // gains. Read by the onSegmentCleared branch above.
  setScoreMultiplier(mul: number): void {
    this.extraScoreMul = Math.max(0, mul);
  }

  // Long-lived reward multiplier. PickupManager composes it from the
  // current difficulty's scoreMul and any active double-pickup window
  // (so a Hard-mode double = 1.25 * 2 = 2.5x). MorphHexGame.startGame
  // resets this to 1.0 then immediately applies the difficulty value.
  setRewardMultiplier(mul: number): void {
    this.rewardMul = Math.max(0, mul);
  }

  reset(): void {
    this.score = 0;
    this.combo = 0;
    this.multiplier = 1;
    this.perfectStreak = 0;
    this.extraScoreMul = 1;
    this.rewardMul = 1;
    this.grazedThisSegment = false;
    this.hud.setCombo(0, 1);
    this.onComboChanged.emit(0);
  }

  private isGraze(wall: import("cannon-es").Body): boolean {
    const sides = shapeOf(this.streamer.getCurrentShape()).sides;
    const expectedFloor = this.rotator.getFloorPanelIndex(sides);
    const idx = panelIndexOf(wall);
    return idx >= 0 && idx !== expectedFloor;
  }
}
